import React from 'react';
import Tool, { type ToolHandler } from '../Tool';
import type { AssistantToolCall } from 'components/framework/models';
import type Agent from '../../agents/Agent';
import { jsonFetch } from 'javascripts/fetch';
// @ts-expect-error fileuploader is not typed
import qq from 'javascripts/fileuploader';
import { showNoticeMessage, showAlertMessage } from 'javascripts/messages';

async function addImageToWhiteboard(toolCall: AssistantToolCall, agent: Agent) {
  const imageUrl = toolCall.content?.panel?.body;
  const assistedWhiteboard = agent.contexts.assistedWhiteboard;

  if (!imageUrl || !assistedWhiteboard) return;

  try {
    const response = await fetch(imageUrl);
    if (!response.ok) return;

    const blob = await response.blob();
    const file = new File([blob], 'generated-image.png', { type: 'image/png' });

    const reader = new FileReader();
    reader.onload = async event => {
      const imageSrc = event.target?.result;
      if (typeof imageSrc === 'string' && imageSrc.startsWith('data:')) {
        await assistedWhiteboard.actionCreateImageShape(
          imageSrc as `data:${string}`,
          file
        );
        showNoticeMessage('Added image to whiteboard');
      }
    };
    reader.readAsDataURL(file);
  } catch (error) {
    console.error('Failed to add image to whiteboard:', error);
  }
}

async function addImageToReference(
  toolCall: AssistantToolCall,
  agent: Agent,
  reference: string
) {
  const imageUrl = toolCall.content?.panel?.body;
  if (!imageUrl) return;

  try {
    const response = await fetch(imageUrl);
    if (!response.ok) return;

    const blob = await response.blob();
    const file = new File([blob], 'generated-image.png', { type: 'image/png' });
    const csrfToken = $('meta[name=csrf-token]').attr('content');

    const uploader = new qq.FileUploaderBasic({
      action: '/attachments',
      params: {
        reactive_client_id: window.reactiveClientId?.(),
        embeddable_id: reference,
      },
      customHeaders: { 'X-CSRF-Token': csrfToken },
      onComplete: async (
        id: string,
        fileName: string,
        responseJSON: { attachmentId: string }
      ) => {
        const embedResponse = await jsonFetch(
          `/assistant/embed/${reference}/attachment`,
          {
            method: 'POST',
            body: JSON.stringify({
              attachment_id: responseJSON.attachmentId,
            }),
          }
        );

        const { success } = (await embedResponse.json()) as {
          success: boolean;
        };

        if (success) {
          showNoticeMessage(`Added image to ${reference}`);
        } else {
          showAlertMessage(`Failed to add image to ${reference}`);
        }
      },
      onError: (id: string, fileName: string, errorReason: string) => {
        console.error('Upload failed:', errorReason);
      },
    });
    uploader.uploadFileList([file]);
  } catch (error) {
    console.error('Failed to process image:', error);
  }
}

const generateImageToolHandler: ToolHandler = {
  getToolActions(toolCall: AssistantToolCall, agent: Agent): React.ReactNode {
    if (toolCall.status !== 'complete' || !toolCall.content?.panel?.body) {
      return null;
    }

    if (toolCall.content.panel.contentType !== 'application/vnd.aha.image') {
      return null;
    }

    const menuItems: React.ReactNode[] = [];

    const references =
      agent.controller.state.model?.currentChat?.messages?.[0]?.scope
        ?.references;

    if (agent.contexts.assistedWhiteboard) {
      menuItems.push(
        <aha-menu-item
          key='whiteboard'
          onClick={() => void addImageToWhiteboard(toolCall, agent)}
        >
          Add to whiteboard
        </aha-menu-item>
      );
    } else if (references) {
      references.forEach(ref => {
        if (!ref.startsWith('Report-')) {
          menuItems.push(
            <aha-menu-item
              key={ref}
              onClick={() => void addImageToReference(toolCall, agent, ref)}
            >
              Add to {ref}
            </aha-menu-item>
          );
        }
      });
    }

    if (menuItems.length > 0) {
      return (
        <aha-menu>
          <aha-button kind='icon' size='mini' slot='control'>
            <i className='fa-regular fa-plus' />
          </aha-button>
          <aha-menu-content>{menuItems}</aha-menu-content>
        </aha-menu>
      );
    }
  },
};

Tool.register('Images::GenerateImageTool', generateImageToolHandler);
